import { useEffect, useState } from 'react';

import { Button } from '@/components';
import { SelectInput } from '@/components/select-input/SelectInput';
import { useAppContext } from '@/context';
import { useUserUpdate } from '@/hooks';
import { useTranslation } from 'react-i18next';
import {
  getPycountryCompatibleCountries,
  getCountryStateCityCompatibleCountryName
} from '@/utils/countryFilter';

import styles from '../Settings.module.css';

interface PayoutSectionProps {
  onUpdate: (user: Api.UserObject) => void;
  user: Api.UserObject;
}

export function PayoutSection({ onUpdate, user }: PayoutSectionProps) {
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);

  const { t } = useTranslation();
  const { setAlert } = useAppContext();

  const { mutate, isLoading } = useUserUpdate({
    tax_country: selectedCountry || '',
    onSuccess: (res) => {
      if (res && (res as Api.UserObject).user_id) {
        onUpdate(res as Api.UserObject);
        setAlert('SAVED');
      }
    }
  });

  useEffect(() => {
    const pycountryCompatibleCountries = getPycountryCompatibleCountries();

    // Convert backend tax_country (alpha2 code or country name) to country-state-city name for display
    const backendCountryName = user.influencer?.tax_country;
    const displayCountryName = backendCountryName
      ? getCountryStateCityCompatibleCountryName(backendCountryName)
      : '';

    const country = pycountryCompatibleCountries.find((c) => c.name === displayCountryName);

    if (country) {
      setSelectedCountry(country.isoCode);
    }
  }, []);

  return (
    <div className={styles.section}>
      <div className={styles.sectionInfo}>
        <div className={styles.sectionInfoTitle}>{t('account.payoutSection.title')}</div>
        <div className={styles.sectionInfoDescription}>
          {t('account.payoutSection.description')}
        </div>
      </div>
      <div className={styles.sectionBody}>
        <div className={styles.sectionTable}>
          <div className={styles.row}>
            <div className={styles.grow}>
              <SelectInput
                value={selectedCountry}
                label={t('account.payoutSection.taxCountry')}
                options={getPycountryCompatibleCountries().map((country) => ({
                  value: country.isoCode,
                  label: `${country.flag} ${country.name}`
                }))}
                required
                showSearch
                onChange={(val) => setSelectedCountry(val)}
              />
            </div>
            <div className={styles.fitContent} />
          </div>
        </div>
        <Button
          theme="cta"
          onClick={mutate}
          isLoading={isLoading}
          disabled={!selectedCountry}
          style={{ width: '140px' }}
        >
          {t('inviteView.addressModal.button')}
        </Button>
      </div>
    </div>
  );
}
